/*global define, require */

"use strict";

/**
 * @typedef {Object} Hook — модуль Require, реализующий концепт хука
 * @property {Function} activate — функция, вызываемая при активации
 * @property {Function} deactivate — функция, вызываемая при деактивации
 */

var
    /** @type {HTMLDocument} */
    doc = document,
    linkHooks,
    CLASS_MARKER = 'h-mod',
    ATTR_HREF = 'href',
    ATTR_MODULE = 'data-module',
    ATTR_ACTIVATE_FUNC = 'data-activate',
    ATTR_DEACTIVATE_FUNC = 'data-deactivate',
    ACTIVATE_FUNC_DEFAULT = 'activate',
    DEACTIVATE_FUNC_DEFAULT = 'deactivate',
    MODULE_PROPERTY_NAME = 'okReqModule',
    BLOCK_ID_PATTERN = /(\?|\&)cmd=([^\&]+)/,
    OK_PREFIX = 'OK/',
    whiteList = null,

    copy = function (from) {
        return Array.prototype.slice.call(from);
    },

    /**
     * @param {string} link
     * @return {string}
     */
    getBlockId = function(link) {
        var match = link.match(BLOCK_ID_PATTERN);
        if (match) {
            return match[2];
        }
    },

    /**
     * @param {*} module
     * @param {string} moduleName
     * @return {*}
     */
    interop = function(module, moduleName) {
        if (module && typeof module === "object") {
            /* Поддержка ES модулей */
            if (module.default) {
                /*
                 * Если явно указан
                 * export default …
                 * будем считать этот экспорт реализацией модуля
                 */
                return module.default;
            }
            if (module[moduleName]) {
                /*
                 * Если модуль содержит именованный экспорт с именем модуля
                 * export moduleName …
                 * будем считать этот экспорт реализацией модуля
                 */
                return module[moduleName];
            }
        }
        /*
         * Классический AMD модуль или ES модуль
         */
        return module;
    },

    /**
     * Создаёт функцию активации хука
     * @param {Element} element
     * @param {string} moduleName
     */
    createInstance = function (element, moduleName) {
        var
            /** @type {string} */
            functionName = element.getAttribute(ATTR_ACTIVATE_FUNC) || ACTIVATE_FUNC_DEFAULT;
        /**
         * @param {Object} module — Require Module
         */
        return function activate(module) {
            /** @type {Hook} */
            var instance;
            module = interop(module, moduleName);
            if (typeof module === "function") {
                instance = new module(element.id, element);
                /* Инстанс хука в св-ве элемента */
                element[MODULE_PROPERTY_NAME] = instance;
            } else {
                /* Синглтон */
                instance = module;
            }
            instance[functionName](element);
            element.activated = true;
        };
    },

    activateElement = function(element, moduleName) {
        var
            /** @type {Function} */
            activate,
            /** @type {Hook} */
            module,
            /** @type {string} */
            moduleId = OK_PREFIX + moduleName;

        if (element.activated || whiteList !== null && whiteList.indexOf(moduleName) === -1) {
            return;
        }

        activate = createInstance(element, moduleName);
        if (require.defined(moduleId)) {
            /* Если модуль определён, получим его из контекста Require и сразу выполним функцию активации */
            module = require(moduleId);
            activate(module);
        } else {
            /* Если модуль НЕ определён, выполним процедуру загрузки и проведём отложенную активацию */
            import(moduleId).then(m => m && m.default || m).then(activate);
        }
    },

    deactivateElement = function(element, moduleName) {
        var
            /** @type {string} */
            moduleId = OK_PREFIX + moduleName;

        if (whiteList !== null && whiteList.indexOf(moduleName) === -1) {
            return;
        }

        if (require.defined(moduleId)) {
            var
                /** @type {string} */
                functionName = element.getAttribute(ATTR_DEACTIVATE_FUNC) || DEACTIVATE_FUNC_DEFAULT,
                /** @type {*} */
                module = require(moduleId),
                /** @type {Hook} */
                instance;
            module = interop(module, moduleName);
            if (typeof module === "function") {
                /* Инстанс хука в св-ве элемента */
                instance = element[MODULE_PROPERTY_NAME];
                element[MODULE_PROPERTY_NAME] = null;
            } else {
                /* Синглтон */
                instance = module;
            }
            if (instance && instance[functionName]) {
                instance[functionName](element);
                element.activated = false;
            }
        }
    },

    /**
     * Проводит поиск и (де?)активацию элементов по маркеру от корневого элемента ДОМ-дерева
     * @param {HTMLElement?} root
     * @param {boolean?} deactivate
     */
    handleLifecycle = function (root, deactivate) {
        var
            /** @type {[]} */
            elements,
            /** @type {number} */
            idx,
            /** @type {HTMLElement} */
            element,
            /** @type {string} */
            moduleName,
            /** @type {string} */
            link;
        root = root || doc;

        elements = copy(root.getElementsByClassName(CLASS_MARKER));
        for (idx = 0; idx < elements.length; idx += 1) {
            element = elements[idx];
            moduleName = element.getAttribute(ATTR_MODULE);
            if (moduleName) {
                if (deactivate) {
                    deactivateElement(element, moduleName)
                } else {
                    activateElement(element, moduleName);
                }
            }
        }

        if (linkHooks) {
            elements = copy(root.getElementsByTagName('a'));
            for (idx = 0; idx < elements.length; idx += 1) {
                element = elements[idx];
                link = element.getAttribute(ATTR_HREF);
                if (link) {
                    var blockId = getBlockId(link);
                    if (blockId) {
                        moduleName = linkHooks[blockId];
                        if (moduleName) {
                            if (deactivate) {
                                deactivateElement(element, moduleName);
                            } else {
                                activateElement(element, moduleName);
                            }
                        }
                    }
                }
            }
        }
    },

    /**
     * @param {HTMLElement} element
     */
    capture = function (element) {
        if (doc.readyState === 'interactive' || doc.readyState === 'complete') {
            // DOMContentLoaded уже сработал
            handleLifecycle(element, false);
        } else {
            doc.addEventListener("DOMContentLoaded", function () {
                handleLifecycle(element, false);
            });
        }
    },
    /**
     * @param {HTMLElement} element
     */
    deactivate = function (element) {
        if (doc.readyState === 'interactive' || doc.readyState === 'complete') {
            // DOMContentLoaded уже сработал
            handleLifecycle(element, true);
        } else {
            doc.addEventListener("DOMContentLoaded", function () {
                handleLifecycle(element, true);
            });
        }
    },
    /**
     * @param {HTMLElement} element
     */
    deactivateOnlyElement = function(element) {
        return deactivate(element);
    };

function setWhiteList(list) {
    whiteList = list;
}

function activateDocument(linksToActivate, unused) {
    if (linksToActivate && linksToActivate.length) {
        linkHooks = {};
        var idx, linkToActivate, parts, blockName, moduleName;
        for (idx = 0; idx < linksToActivate.length; idx += 1) {
            linkToActivate = linksToActivate[idx];
            parts = linkToActivate.split(':');
            blockName = parts[0];
            if (parts.length === 1) {
                moduleName = parts[0];
            } else {
                moduleName = parts[1];
            }
            linkHooks[blockName] = moduleName;
        }
    }
    capture();
}

function captureAndActivate(parentHookId, el) {
    capture(el);
}

function deactivateHookById() {

}

export default { setWhiteList, activate: capture, activateDocument, captureAndActivate, deactivate, deactivateOnlyElement, deactivateHookById };

export { setWhiteList, capture as activate, activateDocument, captureAndActivate, deactivate, deactivateOnlyElement, deactivateHookById };
